import React from 'react';

import { Tooltip } from '@/components/Tooltip';
import { Button } from '@/components/ui/button';
import { cn } from '@/utils/classname';

import type { WizardFooterConfig } from './types';

export interface WizardModalFooterProps {
  config: WizardFooterConfig;
  className?: string;
  primaryDisabledTooltip?: React.ReactNode;
}

/**
 * Wizard-specific modal footer with proper button layout:
 *
 * Layout: [Back (secondary)]  <----flexible space---->  [Cancel (tertiary)] [Next/Save (primary)]
 *
 * - Back button on far left (only shown when canGoBack is true)
 * - Cancel and Primary buttons grouped on the right with gap-2
 * - Cancel is always tertiary (text-only)
 * - Primary is always primary style
 *
 * This component should be used with Modal's hideFooter={true} prop.
 */
export const WizardModalFooter: React.FC<WizardModalFooterProps> = ({
  config,
  className,
  primaryDisabledTooltip,
}) => {
  const { backButton, cancelButton, primaryButton } = config;
  const primary = (
    <Button
      onClick={primaryButton.onClick}
      disabled={primaryButton.disabled}
      className="min-w-24 px-4"
      data-testid="wizard-primary-button"
    >
      {primaryButton.text}
    </Button>
  );

  return (
    <div
      className={cn(
        'flex items-center justify-between rounded-b-lg',
        'mt-6 pt-6 pb-5',
        className
      )}
    >
      {/* Left section: Back button only (or empty space for layout consistency) */}
      <div className="min-w-[100px]">
        {backButton && (
          <Button
            onClick={backButton.onClick}
            disabled={backButton.disabled}
            variant="outline"
            data-testid="wizard-back-button"
          >
            {backButton.label}
          </Button>
        )}
      </div>

      {/* Right section: Cancel + Primary buttons grouped together */}
      <div className="flex items-center gap-2">
        <Button
          onClick={cancelButton.onClick}
          disabled={cancelButton.disabled}
          variant="ghost"
          className="border-0"
          data-testid="wizard-cancel-button"
        >
          {cancelButton.label}
        </Button>
        {primaryButton.disabled && primaryDisabledTooltip ? (
          <Tooltip title={primaryDisabledTooltip}>
            <span
              className="inline-flex cursor-not-allowed [&>button]:pointer-events-none"
              aria-disabled="true"
              tabIndex={0}
            >
              {primary}
            </span>
          </Tooltip>
        ) : (
          primary
        )}
      </div>
    </div>
  );
};
